// LIBRERIE/IMPORTS
import React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

// HOOKS
import { useGetAgentsByAgency } from "../../hooks/useGetAgents";
import { useGetAgencyDetails } from "../../hooks/useGetAgencyDetails";

// COMPONENTI
import SubHeaderDefault from "../Header/subHeader/SubHeaderDefault";
import AgentsSection from "./AgentsSection";
import ServerError from "../Error/ServerError";
import LoadingIndicator from "../Loaders/LoadingIndicator";
import WidgetContacts from "../Widgets/WidgetContacts/WidgetContacts";
import WidgetPartners from "../Widgets/WidgetPartners/WidgetPartners";

// CONFIGURAZIONI
import appConfig from "../../config/appConfig";

interface AboutUsProps {
    agencyId: number;
    showAgents: boolean;
}

const AboutUs: React.FC<AboutUsProps> = ({ agencyId, showAgents }) => {
    const { t } = useTranslation();
    const { agents, loading: agentsLoading, error: agentsError } = useGetAgentsByAgency(agencyId);
    const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(agencyId);

    const staticPartnerUrl = agencyId ? `http://www.casain24ore.it/fromsito.php?agReg=${agencyId}` : 'http://www.casain24ore.it';
    const staticPartner = { url: staticPartnerUrl, data: { imgSrc: '/assets/images/c24.png', imgAlt: 'Network Immobiliare' } };
    const rawDynamicPartnersData = appConfig.content.partners;
    const validDynamicPartnersData = rawDynamicPartnersData.filter(p => p && p.imgSrc && p.imgSrc.trim() !== '');
    const formattedDynamicPartners = validDynamicPartnersData.map(p => ({ url: p.url || '#', data: { imgSrc: p.imgSrc as string, imgAlt: p.imgAlt || 'Partner Logo' } }));
    const allPartnersForWidget = [staticPartner, ...formattedDynamicPartners];

    const reviewsLink = "https://www.google.com/search?sca_esv=0e39c597175f10a4&hl=it&authuser=1&uds=AOm0WdE2fekQnsyfYEw8JPYozOKz5jtIRW3dqvmQ84hgRZJx7A2qZLcf6u22-mL378pg9ml1FONix_jvp8wZda0H7cWdTDFojz-mb4TWOhilF6fnwo1yz-Xhu82bVR6_X3HjynFsqzWOIA-Mvkzlp24wtJHWX6ORBOk8qlQx24wq_q5l9At7QNSIy0-JD7h4fxCg0QNTtOCe2rxjO9Ca73esqvouLLY0NaJUWA78QeCrbFw0iraW8QgvtiIDzOr09pYRkuzo0SooM3c8pmw-LU12zBzE_p_k_g_ybRpoHI93_vpVa27eRtCAExTPCKv7res0Tju5Hc7YVycDwKvZiJ3IeJNswCn2Rg&q=Medimmobiliare+di+Chiara+Crott+Recensioni&si=AMgyJEuzsz2NflaaWzrzdpjxXXRaJ2hfdMsbe_mSWso6src8swGwXfkuoUgMsZp3AdVzz2TMLXwVBvMVM9ynKAT8gxG6BkrU7Ed0mYUn-58joD1m-YDk2F3j_7xaCUgdtY1Jme-DPfnIU6F7u5c_Lh0pOMcN1hgc_g%3D%3D&authuser=1&sa=X&ved=2ahUKEwj16cTj5MGOAxWB_rsIHUXGFKMQ_4MLegQIOhAN&biw=1854&bih=963&dpr=1";

    if (agencyLoading) return <LoadingIndicator />;
    if (agencyError) return <ServerError />;
    if (agentsLoading && showAgents) return <LoadingIndicator />;
    if (agentsError && showAgents) console.error("Error fetching agents:", agentsError);

    return (
        <>
            <Helmet>
                <title>{t('aboutUsPage.pageTitle')} | {agency?.agencyName || 'Agenzia Immobiliare'}</title>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Lato:wght@300;400&display=swap" rel="stylesheet" />
            </Helmet>

            <SubHeaderDefault title={t('aboutUsPage.pageTitle')} isSeo={false} />

            <div className="about-us">
                <section className="space-ptb">
                    <div className="container">
                        <div className="row">
                            <div className="col-12">
                                <p dangerouslySetInnerHTML={{ __html: t('aboutUsPage.para1') as string }}></p>
                                <p dangerouslySetInnerHTML={{ __html: t('aboutUsPage.para2') as string }}></p>
                                <div className="row mb-4 align-items-center">
                                    <div className="col-12 col-lg-6 mb-4">
                                        <p className="mb-1">{t('aboutUsPage.pre-list1')}</p>
                                        <ul className="list-unstyled">
                                            <li>
                                                <span className="pr-2">&#9989;</span>
                                                {t('aboutUsPage.list1-Element1')}
                                            </li>
                                            <li>
                                                <span className="pr-2">&#9989;</span>
                                                {t('aboutUsPage.list1-Element2')}
                                            </li>
                                            <li>
                                                <span className="pr-2">&#9989;</span>
                                                {t('aboutUsPage.list1-Element3')}
                                            </li>
                                            <li>
                                                <span className="pr-2">&#9989;</span>
                                                {t('aboutUsPage.list1-Element4')}
                                            </li>
                                        </ul>
                                        <p>{t('aboutUsPage.para3')}</p>
                                        <p dangerouslySetInnerHTML={{ __html: t('aboutUsPage.para4') as string }}></p>
                                        <p className="mb-1">{t('aboutUsPage.pre-list2')}</p>
                                        <ul className="pl-3 mb-4">
                                            <li>
                                                {t('aboutUsPage.list2-Element1')}
                                            </li>
                                            <li>
                                                {t('aboutUsPage.list2-Element2')}
                                            </li>
                                            <li>
                                                {t('aboutUsPage.list2-Element3')}
                                            </li>
                                            <li>
                                                {t('aboutUsPage.list2-Element4')}
                                            </li>
                                        </ul>
                                        <a className="btn btn-primary btn-md" href={reviewsLink} target="_blank">
                                            {t('aboutUsPage.btn-text')}
                                        </a>
                                    </div>
                                    <div className="col-12 col-lg-6 text-center">
                                        <img className="img-fluid" src="../assets/images/aboutUs/chiara.jpg" alt="chiara img" />
                                    </div>
                                </div>
                            </div>
                            {/* Colonna Sidebar (Widgets) */}
                            <div className="col-12 col-md-6">
                                <WidgetContacts
                                    agencyDataAddress={agency?.address_details?.address ?? ''}
                                    agencyDataCity={agency?.address_details?.city_name ?? ''}
                                    agencyDataComuneCapoluogo={agency?.address_details?.city_name === agency?.address_details?.province_name}
                                    agencyDataProvinciaSigla={agency?.address_details?.sigla ?? ''}
                                    agencyDataEmail={appConfig.agency.agencyEmail}
                                    agencyDataEmailShow={appConfig.ui.agencyWidget.showEmail}
                                    agencyDataPhone1={agency?.phone1 ?? ''}
                                    agencyDataPhone2={agency?.phone2 ?? ''}
                                    agencyDataInternationalPrefix={appConfig.agency.intlPrefix ?? '+39'}
                                    agencyDataMobile={agency?.mobile ?? ''}
                                    agencyDataMobile2={agency?.mobile2 ?? ''}
                                    agencyDataFax={agency?.fax ?? ''}
                                    agencySocialShow={appConfig.ui.agencyWidget.showSocialLinks}
                                    agencyDataWhatsapp={appConfig.socialLinks.whatsapp}
                                    agencyDataFacebook={appConfig.socialLinks.facebook}
                                    agencyDataInstagram={appConfig.socialLinks.instagram}
                                    agencyDataTwitter={appConfig.socialLinks.twitter}
                                    agencyDataLinkedin={appConfig.socialLinks.linkedin}
                                    agencyDataPinterest={appConfig.socialLinks.pinterest}
                                    agencyDataYoutube={appConfig.socialLinks.youtube}
                                />
                            </div>
                            <div className="col-12 col-md-6">
                                <WidgetPartners partners={allPartnersForWidget} />
                            </div>
                        </div>
                    </div>
                </section>
                {showAgents && agentsError && (
                    <div className="alert alert-danger">{t('aboutUsPage.agentsError', 'Errore nel caricamento degli agenti.')}</div>
                )}
                <AgentsSection
                    showAgents={showAgents}
                    agents={agents}
                    agentsLoading={agentsLoading}
                    agentsError={agentsError}
                />
            </div>
        </>
    );
};
export default AboutUs;